import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@enums/queryKeys';
import { useDrawRequestMessageApi } from '@hooks/api/useDrawRequestMessageApi';
import { DrawRequestMessage } from '@models/drawRequests/drawRequestMessage';
import { OriginatorTypes } from '@enums/originatorTypes';
import { RecipientTypes } from '@enums/recipientTypes';
import ChatHubConnector from '@services/chatHubConnector';
import { MessageObject } from '@services/chatHubConnector';
import { useApp } from '@context/AppContext';
import { DRAW_REQUEST_ENTITY_TYPE } from '@constants/drawManagerConstants';

const getInfiniteQueryKey = (drawRequestId: string, sourceRecId: string) => [
  QueryKeys.DRAW_REQUEST_MESSAGES,
  drawRequestId,
  sourceRecId,
  'infinite',
];

const addMessageToLastPage = (oldData: any, message: DrawRequestMessage) => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    pages: [
      ...oldData.pages.slice(0, -1),
      [...(oldData.pages[oldData.pages.length - 1] || []), message],
    ],
  };
};

const removeMessageFromAllPages = (oldData: any, messageId: string) => {
  if (!oldData) return oldData;
  return {
    ...oldData,
    pages: oldData.pages.map((page: DrawRequestMessage[]) =>
      page.filter(msg => msg.recId !== messageId)
    ),
  };
};

interface UseDrawRequestMessageHubProps {
  drawRequestId: string;
  sourceRecId: string;
  account?: string;
}

export const useDrawRequestMessageHub = ({
  drawRequestId,
  sourceRecId,
  account,
}: UseDrawRequestMessageHubProps) => {
  const { companyId } = useApp();
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();
  const hubRef = useRef(ChatHubConnector());

  const {
    data: infiniteData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDrawRequestMessageApi.useFetchMessagesInfinite(
    drawRequestId,
    sourceRecId
  );

  const messages: DrawRequestMessage[] = infiniteData?.pages.flat() || [];

  const sendMessageMutation = useDrawRequestMessageApi.useSendMessage();

  useEffect(() => {
    const hub = hubRef.current;

    const checkConnection = () => {
      const connected = hub.isHubConnected();
      setIsConnected(connected);
      return connected;
    };

    // Check connection state immediately
    checkConnection();

    // Track if we've already joined to prevent duplicates
    let hasJoinedConversation = false;
    let connectionCheckInterval: NodeJS.Timeout | null = null;
    let fallbackTimeout: NodeJS.Timeout | null = null;

    const joinConversationIfReady = async () => {
      // Prevent duplicate joins
      if (hasJoinedConversation || !hub.isHubConnected()) {
        return;
      }

      try {
        await hub.joinConversation(DRAW_REQUEST_ENTITY_TYPE, drawRequestId);
        hasJoinedConversation = true;

        // Clean up polling once we've successfully joined
        if (connectionCheckInterval) {
          clearInterval(connectionCheckInterval);
          connectionCheckInterval = null;
        }
        if (fallbackTimeout) {
          clearTimeout(fallbackTimeout);
          fallbackTimeout = null;
        }
      } catch (error) {
        console.error('Failed to join conversation:', error);
      }
    };

    // Handle connection state changes via event listener (primary method)
    const handleConnectionStateChange = () => {
      checkConnection();
      joinConversationIfReady();
    };

    // Update the existing unsubscribe to use the improved handler
    const unsubscribeStateChange = hub.onConnectionStateChange(
      handleConnectionStateChange
    );

    // Try to join immediately if already connected
    if (hub.isHubConnected()) {
      joinConversationIfReady();
    } else {
      // Fallback: Set up polling only if connection state events don't fire quickly enough
      connectionCheckInterval = setInterval(() => {
        joinConversationIfReady();
      }, 500); // Reduced frequency to 500ms since events should handle most cases

      // Clean up fallback polling after 10 seconds
      fallbackTimeout = setTimeout(() => {
        if (connectionCheckInterval) {
          clearInterval(connectionCheckInterval);
          connectionCheckInterval = null;
        }
      }, 10000);
    }

    const handleReceiveMessage = (messageObj: MessageObject) => {
      if (
        messageObj.entityId === drawRequestId &&
        messageObj.entityType === DRAW_REQUEST_ENTITY_TYPE &&
        messageObj.originatorType === OriginatorTypes.Company &&
        messageObj.recipientType === RecipientTypes.Borrower
      ) {
        const drawRequestMessage: DrawRequestMessage = {
          recId: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          message: messageObj.message,
          sentDateTime: new Date(messageObj.sentTime),
          originatorType: messageObj.originatorType as OriginatorTypes,
          recipientType: messageObj.recipientType as RecipientTypes,
          drawRequestId: messageObj.entityId,
        };

        queryClient.setQueryData(
          getInfiniteQueryKey(drawRequestId, sourceRecId),
          (oldData: any) => addMessageToLastPage(oldData, drawRequestMessage)
        );
      }
    };

    hub.onReceiveMessage(handleReceiveMessage);

    const periodicCheckInterval = setInterval(checkConnection, 5000);

    return () => {
      unsubscribeStateChange();
      hub.offReceiveMessage(handleReceiveMessage);
      hub.leaveConversation(DRAW_REQUEST_ENTITY_TYPE, drawRequestId);
      if (connectionCheckInterval) {
        clearInterval(connectionCheckInterval);
      }
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
      }
      if (periodicCheckInterval) {
        clearInterval(periodicCheckInterval);
      }
    };
  }, [drawRequestId, sourceRecId, queryClient]);

  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim()) return;

      const hub = hubRef.current;
      if (!hub.isHubConnected()) {
        return;
      }

      const request = {
        drawRequestRecId: drawRequestId,
        sourceRecId: sourceRecId,
        message: message.trim(),
        subject: null,
        importance: 1,
        account: account || '',
        companyId: companyId,
      };

      const optimisticMessage: DrawRequestMessage = {
        recId: `temp-${Date.now()}`,
        message: message.trim(),
        sentDateTime: new Date(),
        originatorType: OriginatorTypes.Borrower,
        recipientType: RecipientTypes.Company,
        drawRequestId: drawRequestId,
      };

      queryClient.setQueryData(
        getInfiniteQueryKey(drawRequestId, sourceRecId),
        (oldData: any) => addMessageToLastPage(oldData, optimisticMessage)
      );

      try {
        sendMessageMutation.mutate(request, {
          onError: () => {
            queryClient.setQueryData(
              getInfiniteQueryKey(drawRequestId, sourceRecId),
              (oldData: any) =>
                removeMessageFromAllPages(oldData, optimisticMessage.recId)
            );
          },
          onSuccess: () => {
            try {
              const messageObj = {
                entityType: DRAW_REQUEST_ENTITY_TYPE,
                entityId: drawRequestId,
                message: message.trim(),
                sentTime: new Date().toISOString(),
                originatorType: OriginatorTypes.Borrower,
                recipientType: RecipientTypes.Company,
              };

              hub.sendMessage(
                DRAW_REQUEST_ENTITY_TYPE,
                drawRequestId,
                messageObj
              );
            } catch (error) {
              console.error('Failed to send message via hub:', error);
            }
          },
        });
      } catch (error) {
        queryClient.setQueryData(
          getInfiniteQueryKey(drawRequestId, sourceRecId),
          (oldData: any) =>
            removeMessageFromAllPages(oldData, optimisticMessage.recId)
        );
      }
    },
    [
      drawRequestId,
      sourceRecId,
      account,
      companyId,
      sendMessageMutation,
      queryClient,
      hubRef,
    ]
  );

  const loadMoreMessages = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    messages,
    isLoading,
    isConnected,
    sendMessage,
    isSending: sendMessageMutation.isPending,
    loadMoreMessages,
    hasNextPage,
    isFetchingNextPage,
  };
};
